import { signedFetch } from './signedFetch';

// API Gateway URLs per environment
const API_GATEWAY_URL = window.location.hostname.includes('integ-')
  ? 'https://bm1o4jvrkj.execute-api.us-west-2.amazonaws.com' // Beta
  : 'https://jop1rjczze.execute-api.us-east-1.amazonaws.com'; // Prod

export interface MissingMetric {
  name: string;
  priority: string;
  tag: string;
  silent?: string;
  what?: string;
  why?: string;
  current?: string;
  where?: string;
  implement?: string;
  impact?: string;
  section?: string; // 'service' | 'dependency' | 'auto-rollback'
  type?: string; // 'individual' | 'derived'
  crLink?: string;
}

export interface MetricsResponse {
  pipeline: string;
  metrics: MissingMetric[];
  feedback: Record<string, string>;
  autoRollbackAnalysis?: any;
}

export async function listPipelines(): Promise<string[]> {
  const res = await signedFetch(`${API_GATEWAY_URL}/pipelines`);
  const data = await res.json();
  return data.pipelines || [];
}

export async function getMetrics(pipeline: string): Promise<MetricsResponse> {
  const res = await signedFetch(`${API_GATEWAY_URL}/metrics/${encodeURIComponent(pipeline)}`);
  const data = await res.json();
  // Fetch live feedback from API (SigV4 signed)
  let feedback: Record<string, string> = {};
  try {
    const fbRes = await signedFetch(`${API_GATEWAY_URL}/feedback/${encodeURIComponent(pipeline)}`);
    if (fbRes.ok) {
      const fbData = await fbRes.json();
      feedback = fbData.feedbackJson ? JSON.parse(fbData.feedbackJson) : fbData;
    }
  } catch {}
  return {
    pipeline: data.pipeline,
    metrics: (() => {
      const parsed = typeof data.metricsJson === 'string' ? JSON.parse(data.metricsJson) : (data.metrics || []);
      // Handle both flat array and {metrics: [...], autoRollbackAnalysis: {...}} formats
      if (Array.isArray(parsed)) return parsed;
      if (parsed && Array.isArray(parsed.metrics)) return parsed.metrics;
      return [];
    })(),
    autoRollbackAnalysis: (() => {
      const parsed = typeof data.metricsJson === 'string' ? JSON.parse(data.metricsJson) : data;
      if (parsed && parsed.autoRollbackAnalysis) return parsed.autoRollbackAnalysis;
      return null;
    })(),
    feedback,
  };
}

export async function submitFeedback(pipeline: string, metricName: string, helpful: string): Promise<void> {
  await signedFetch(`${API_GATEWAY_URL}/feedback/${encodeURIComponent(pipeline)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ metricName, helpful }),
  });
}

const REQUESTS_API = `${API_GATEWAY_URL}/requests`;

export interface AnalysisRequest {
  pipeline: string;
  requestedBy: string;
  status: string;
  requestedAt: string;
}

export async function submitAnalysisRequest(pipeline: string, requestedBy: string): Promise<void> {
  await signedFetch(REQUESTS_API, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ pipeline, requestedBy }),
  });
}

export async function getAnalysisRequests(): Promise<AnalysisRequest[]> {
  try {
    const res = await signedFetch(REQUESTS_API);
    if (!res.ok) return [];
    const data = await res.json();
    return data.requests || [];
  } catch { return []; }
}

export async function updateRequestStatus(pipeline: string, requestedAt: string, status: string): Promise<void> {
  await signedFetch(REQUESTS_API, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ pipeline, requestedAt, status }),
  });
}

export async function deleteAnalysisRequest(pipeline: string, requestedAt: string): Promise<void> {
  const params = new URLSearchParams({ pipeline, requestedAt });
  await signedFetch(`${REQUESTS_API}?${params.toString()}`, {
    method: 'DELETE',
  });
}
